import { useMutation, useQueryClient } from '@tanstack/react-query';

import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import {
  PutGpaCalculationParametersResponse,
  PutGpaCalculationParametersRequestBody,
} from '@ab/ams-school-api/types/private/v1';
import { hasKey } from '~/utils';

export type GpaCalculatorParams = Omit<PutGpaCalculationParametersRequestBody['data']['attributes'], 'type'>;

export function useUpsertApplicationGpaConfiguration(applicationId: string) {
  const request = useRequest();
  const queryClient = useQueryClient();
  const { isLoading, mutateAsync } = useMutation(['gpa', applicationId], async (payload: GpaCalculatorParams) => {
    const response = await request<unknown>(
      `${config.apiHost}/private/v1/applications/${applicationId}/gpa_calculation_parameters`,
      {
        method: 'PUT',
        body: {
          data: {
            id: applicationId,
            type: 'applicationGpaCalculationParameters',
            attributes: payload,
          },
        } as unknown as object,
        isExpectedResponse,
      }
    );

    // Invalidate the application query to refetch updated data with GPA values
    queryClient.invalidateQueries(['application', applicationId]);

    return response;
  });

  // Check if the application query is currently being refetched after invalidation
  const isRefetchingApplication = queryClient.isFetching(['application', applicationId]) > 0;

  return {
    calculateGpa: mutateAsync,
    isLoadingCalculateGpa: isLoading || isRefetchingApplication,
  };
}

function isExpectedResponse(res: unknown): res is PutGpaCalculationParametersResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
